import writeXlsxFile, { type SheetData, type CellObject } from 'write-excel-file/browser';

interface ParsedCell {
  type: 'number' | 'string';
  value: number | string;
  format?: string;
}

const CURRENCY_RE = /^R\$\s*(-?[\d.]+,\d{2})$/;
const PERCENT_RE = /^(-?[\d.]+(?:,\d+)?)\s*%$/;
const NUMBER_RE = /^-?\d{1,3}(?:\.\d{3})*(?:,\d+)?$|^-?\d+(?:,\d+)?$/;
const DATE_RE = /^(\d{2})\/(\d{2})\/(\d{4})$/;

function ptBrToNumber(text: string): number | null {
  const cleaned = text.replace(/\./g, '').replace(',', '.');
  const n = parseFloat(cleaned);
  return Number.isFinite(n) ? n : null;
}

export function parseCellValue(raw: string): ParsedCell {
  const text = (raw ?? '').trim();
  if (!text || text === '—' || text === '-') {
    return { type: 'string', value: '' };
  }

  const curr = text.match(CURRENCY_RE);
  if (curr) {
    const n = ptBrToNumber(curr[1]!);
    if (n !== null) return { type: 'number', value: n, format: '"R$ "#,##0.00' };
  }

  const pct = text.match(PERCENT_RE);
  if (pct) {
    const n = ptBrToNumber(pct[1]!);
    if (n !== null) return { type: 'number', value: n / 100, format: '0.0%' };
  }

  if (NUMBER_RE.test(text)) {
    const n = ptBrToNumber(text);
    if (n !== null) {
      const hasDecimals = text.includes(',');
      return { type: 'number', value: n, format: hasDecimals ? '#,##0.00' : '#,##0' };
    }
  }

  const date = text.match(DATE_RE);
  if (date) {
    return { type: 'string', value: text };
  }

  return { type: 'string', value: text };
}

function tableRows(table: HTMLTableElement): string[][] {
  const out: string[][] = [];
  for (const tr of Array.from(table.querySelectorAll('tr'))) {
    const cells = Array.from(tr.querySelectorAll('th, td'));
    if (!cells.length) continue;
    out.push(cells.map((cell) => (cell.textContent ?? '').trim()));
  }
  return out;
}

function buildSheet(rawRows: string[][]): SheetData {
  if (!rawRows.length) return [];
  const [header, ...body] = rawRows;
  const headerCells = header ?? [];

  const headerRow: CellObject[] = headerCells.map((label) => ({
    type: String,
    value: label,
    fontWeight: 'bold',
    align: 'center',
    backgroundColor: '#f5f5f5',
  }));

  const colCount = headerCells.length;
  const numericCols = new Set<number>();
  for (let c = 0; c < colCount; c++) {
    let numericCount = 0;
    let totalCount = 0;
    for (const row of body) {
      const cell = row[c];
      if (cell == null || cell === '') continue;
      totalCount++;
      if (parseCellValue(cell).type === 'number') numericCount++;
    }
    if (totalCount > 0 && numericCount / totalCount >= 0.6) numericCols.add(c);
  }

  const dataRows: CellObject[][] = body.map((row) =>
    row.map((rawValue, colIdx): CellObject => {
      const parsed = parseCellValue(rawValue);
      if (parsed.type === 'number') {
        return {
          type: Number,
          value: parsed.value as number,
          format: parsed.format,
          align: 'right',
        };
      }
      return {
        type: String,
        value: parsed.value as string,
        align: numericCols.has(colIdx) ? 'right' : 'left',
      };
    }),
  );

  return [headerRow, ...dataRows];
}

export interface XlsxExportOptions {
  fileName: string;
  sheetNames?: string[];
}

/**
 * Export one or more HTML tables to a real .xlsx file. Each table becomes
 * a separate sheet. Cell types are auto-detected (currency, percent, number,
 * text) with appropriate Excel number formats applied.
 */
export async function exportTablesToXlsx(
  tables: HTMLTableElement[],
  opts: XlsxExportOptions,
): Promise<void> {
  if (!tables.length) return;
  const sheets: SheetData[] = tables.map((t) => buildSheet(tableRows(t)));
  const sheetNames =
    opts.sheetNames && opts.sheetNames.length === sheets.length
      ? opts.sheetNames
      : sheets.map((_, i) => (sheets.length === 1 ? 'Dados' : `Tabela ${i + 1}`));

  if (sheets.length === 1) {
    await writeXlsxFile(sheets[0]!, {
      fileName: opts.fileName,
      sheet: sheetNames[0]!,
    });
    return;
  }

  await writeXlsxFile(sheets, {
    fileName: opts.fileName,
    sheets: sheetNames,
  });
}
